import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { AUTH, FORGOT_PASSCODE, FORGOT_MASTER_PASSCODE } from "@/constants/testIds";
import PasswordInput from "@/components/PasswordInput";

const COURSE_BG =
  "/brand/proshop-apparel.jpg";

const PASSCODE_MIN = 5;
const PASSCODE_MAX = 10;

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [mode, setMode] = useState(null); // 'login' | 'setup' | null while loading
  const [passcode, setPasscode] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Forgot-passcode panel state
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotInput, setForgotInput] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotMsg, setForgotMsg] = useState(null);

  // Forgot-MASTER-passcode panel state (platform owner recovery)
  const [forgotMasterOpen, setForgotMasterOpen] = useState(false);
  const [forgotMasterBusy, setForgotMasterBusy] = useState(false);
  const [forgotMasterMsg, setForgotMasterMsg] = useState(null);

  useEffect(() => {
    if (user && user.role === "club_admin") {
      navigate("/admin", { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    api
      .get("/auth/back-office/status")
      .then((r) => setMode(r.data.configured ? "login" : "setup"))
      .catch(() => setMode("login"));
  }, []);

  const valid = (s) => s.length >= PASSCODE_MIN && s.length <= PASSCODE_MAX;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!valid(passcode)) {
      setError(`Passcode must be ${PASSCODE_MIN}–${PASSCODE_MAX} characters.`);
      return;
    }
    if (mode === "setup" && passcode !== confirm) {
      setError("Passcodes do not match.");
      return;
    }
    setSubmitting(true);
    try {
      const endpoint =
        mode === "setup" ? "/auth/back-office/setup" : "/auth/back-office/login";
      const { data } = await api.post(endpoint, { passcode });
      setUser(data.user);
      // iter71b · When the SA passcode is used, land on the super admin
      // command center rather than the club-side back office.
      if (data.super_admin) {
        navigate("/super-admin", { replace: true });
      } else {
        navigate("/admin", { replace: true });
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const requestForgot = async () => {
    if (!forgotInput.trim()) {
      setForgotMsg({ type: "error", text: "Enter your club's contact email or slug." });
      return;
    }
    setForgotBusy(true);
    setForgotMsg(null);
    try {
      const { data } = await api.post("/auth/back-office/forgot-passcode", {
        email_or_slug: forgotInput.trim(),
      });
      setForgotMsg({
        type: "success",
        text: data.detail || "If a club email is on file, a temporary passcode has been sent.",
      });
    } catch (err) {
      setForgotMsg({
        type: "error",
        text: formatApiErrorDetail(err.response?.data?.detail) || err.message,
      });
    } finally {
      setForgotBusy(false);
    }
  };

  const requestMasterRecovery = async () => {
    setForgotMasterBusy(true);
    setForgotMasterMsg(null);
    try {
      const { data } = await api.post("/auth/back-office/master-recovery");
      setForgotMasterMsg({
        type: "success",
        text:
          data.detail ||
          "If a recovery email is configured, the master passcode has been sent.",
      });
    } catch (err) {
      setForgotMasterMsg({
        type: "error",
        text: formatApiErrorDetail(err.response?.data?.detail) || err.message,
      });
    } finally {
      setForgotMasterBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0f1a14] relative overflow-hidden text-[#F9F8F4]">
      <img
        src={COURSE_BG}
        alt=""
        className="absolute inset-0 w-full h-full object-cover opacity-25"
      />
      <div className="absolute inset-0 bg-gradient-to-br from-[#0f1a14] via-[#1A422D]/85 to-[#0f1a14]" />

      {/* EST. 1928 hallmark — top-right counterweight */}
      <div
        className="hidden sm:block absolute top-10 right-10 text-right fade-up"
        style={{ animationDelay: "0.05s" }}
      >
        <div className="text-[12px] uppercase tracking-[0.32em] font-semibold" style={{ color: "#D9C68A" }}>
          Established
        </div>
        <div className="font-serif italic text-2xl text-[#F9F8F4] mt-1">Anno 2026</div>
      </div>

      <div className="relative max-w-md mx-auto px-6 py-16 sm:py-24 fade-up">
        <div className="text-center">
          <img
            src="/logo.png"
            alt="My Crest Club"
            data-testid="adminlogin-logo"
            className="h-24 sm:h-28 mx-auto mb-4 drop-shadow-[0_8px_24px_rgba(0,0,0,0.4)]"
          />
          <div className="text-[13px] uppercase tracking-[0.32em] font-semibold" style={{ color: "#D9C68A" }}>Staff Portal</div>
          <h1 className="font-serif text-6xl sm:text-7xl text-[#F9F8F4] mt-4 leading-none">
            The Back Office
          </h1>
          <p className="mt-5 text-[#D9D2B6] text-base max-w-sm mx-auto leading-relaxed">
            {mode === "setup"
              ? "First visit — choose a shared passcode (5–10 characters). Anyone on staff will use it from then on."
              : "Enter the shared staff passcode to confirm orders, message members, and place vendor work."}
          </p>
        </div>

        {error && (
          <div
            data-testid={AUTH.errorBanner}
            className="mt-8 px-4 py-3 border border-[#B59A5A] bg-[#1A422D]/60 text-[#F9F8F4] text-sm"
          >
            {error}
          </div>
        )}

        {mode === null ? (
          <div className="mt-10 text-center overline" style={{ color: "#D9C68A" }}>
            Checking entrance…
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="mt-10 border border-[#B59A5A]/40 bg-[#0f1a14]/70 backdrop-blur-sm p-8 sm:p-10"
          >
            <div className="space-y-5">
              <div>
                <label
                  className="block text-[13px] uppercase tracking-[0.24em] font-semibold mb-3"
                  style={{ color: "#D9C68A" }}
                >
                  {mode === "setup" ? "Choose Passcode" : "Passcode"}
                </label>
                <PasswordInput
                  required
                  minLength={PASSCODE_MIN}
                  maxLength={PASSCODE_MAX}
                  autoComplete={mode === "setup" ? "new-password" : "current-password"}
                  value={passcode}
                  onChange={(e) => setPasscode(e.target.value)}
                  data-testid={AUTH.loginPassword}
                  eyeClassName="text-[#9aa39a] hover:text-[#D9C68A]"
                  className="w-full bg-[#0f1a14] border border-[#B59A5A]/40 text-[#F9F8F4] placeholder-[#7a7a6a] h-12 px-3 focus:outline-none focus:border-[#B59A5A] tracking-[0.4em] text-center text-lg"
                  placeholder="• • • • •"
                />
                <p className="mt-2 text-[10px] uppercase tracking-[0.18em]" style={{ color: "#9aa39a" }}>
                  {passcode.length}/{PASSCODE_MAX}
                </p>
              </div>

              {mode === "setup" && (
                <div>
                  <label
                    className="block text-[0.7rem] uppercase tracking-[0.22em] font-semibold mb-2"
                    style={{ color: "#D9C68A" }}
                  >
                    Confirm Passcode
                  </label>
                  <PasswordInput
                    required
                    minLength={PASSCODE_MIN}
                    maxLength={PASSCODE_MAX}
                    autoComplete="new-password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    data-testid="back-office-confirm-passcode"
                    eyeClassName="text-[#9aa39a] hover:text-[#D9C68A]"
                    className="w-full bg-[#0f1a14] border border-[#B59A5A]/40 text-[#F9F8F4] placeholder-[#7a7a6a] h-12 px-3 focus:outline-none focus:border-[#B59A5A] tracking-[0.4em] text-center text-lg"
                    placeholder="• • • • •"
                  />
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                data-testid={AUTH.loginSubmit}
                className="w-full mt-4 bg-[#B59A5A] hover:bg-[#A6894B] text-[#0f1a14] font-semibold uppercase tracking-[0.24em] text-base py-3.5 transition-colors"
              >
                {submitting
                  ? mode === "setup"
                    ? "Setting passcode…"
                    : "Entering…"
                  : mode === "setup"
                    ? "Set Passcode & Enter"
                    : "Enter the Back Office"}
              </button>
            </div>

            {mode === "login" && (
              <div className="mt-6 text-center space-y-4">
                {!forgotOpen ? (
                  <button
                    type="button"
                    onClick={() => { setForgotOpen(true); setForgotMsg(null); }}
                    data-testid={FORGOT_PASSCODE.trigger}
                    className="text-[11px] uppercase tracking-[0.22em] text-[#9aa39a] hover:text-[#D9C68A] underline-offset-4 hover:underline"
                  >
                    Forgot the passcode?
                  </button>
                ) : (
                  <div
                    data-testid={FORGOT_PASSCODE.panel}
                    className="border border-[#B59A5A]/30 bg-[#0f1a14]/80 p-5 text-left"
                  >
                    <div className="overline" style={{ color: "#D9C68A" }}>Recovery</div>
                    <p className="text-sm text-[#D9D2B6] mt-2 leading-relaxed">
                      Enter your club&rsquo;s contact email (or its slug). We&rsquo;ll email a
                      one-time temporary passcode (good for 30 minutes) to the club&rsquo;s
                      contact email on file. Use it to sign in, then immediately set a new
                      permanent passcode from inside the Back Office.
                    </p>
                    <input
                      type="text"
                      value={forgotInput}
                      onChange={(e) => setForgotInput(e.target.value)}
                      placeholder="contact@yourclub.com  or  yourclub-slug"
                      data-testid="forgot-passcode-input"
                      disabled={forgotBusy || (forgotMsg && forgotMsg.type === "success")}
                      className="mt-3 w-full px-3 py-2 bg-[#0f1a14] border border-[#B59A5A]/40 text-[#F9F8F4] text-sm font-mono focus:outline-none focus:border-[#D9C68A] disabled:opacity-60"
                    />
                    {forgotMsg && (
                      <div
                        data-testid={FORGOT_PASSCODE.feedback}
                        className={`mt-4 px-3 py-2 text-sm border ${
                          forgotMsg.type === "success"
                            ? "border-[#B59A5A] bg-[#1A422D]/60 text-[#F9F8F4]"
                            : "border-[#A33] bg-[#5a1a1a]/60 text-[#FFCFCF]"
                        }`}
                      >
                        {forgotMsg.text}
                      </div>
                    )}
                    <div className="mt-4 flex items-center justify-end gap-3">
                      <button
                        type="button"
                        onClick={() => { setForgotOpen(false); setForgotMsg(null); }}
                        data-testid={FORGOT_PASSCODE.cancel}
                        className="text-[11px] uppercase tracking-[0.22em] text-[#9aa39a] hover:text-[#D9C68A]"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={requestForgot}
                        disabled={forgotBusy || (forgotMsg && forgotMsg.type === "success")}
                        data-testid={FORGOT_PASSCODE.submit}
                        className="px-4 py-2 text-[11px] uppercase tracking-[0.22em] bg-[#B59A5A] text-[#0f1a14] font-semibold hover:bg-[#A6894B] disabled:opacity-60 transition-colors"
                      >
                        {forgotBusy ? "Sending…" : (forgotMsg && forgotMsg.type === "success" ? "Sent" : "Email Temporary Passcode")}
                      </button>
                    </div>
                  </div>
                )}

                {/* Platform-owner master passcode recovery — sends master code
                    to the configured MASTER_PASSCODE_RECOVERY_EMAIL only. */}
                {!forgotMasterOpen ? (
                  <div>
                    <button
                      type="button"
                      onClick={() => { setForgotMasterOpen(true); setForgotMasterMsg(null); }}
                      data-testid={FORGOT_MASTER_PASSCODE.trigger}
                      className="text-[11px] uppercase tracking-[0.22em] text-[#9aa39a] hover:text-[#D9C68A] underline-offset-4 hover:underline"
                    >
                      Forgot the master passcode?
                    </button>
                  </div>
                ) : (
                  <div
                    data-testid={FORGOT_MASTER_PASSCODE.panel}
                    className="border border-[#B59A5A]/30 bg-[#0f1a14]/80 p-5 text-left"
                  >
                    <div className="overline" style={{ color: "#D9C68A" }}>Platform Master Recovery</div>
                    <p className="text-sm text-[#D9D2B6] mt-2 leading-relaxed">
                      Sends the current platform master passcode (cross-club access) to the
                      platform owner&rsquo;s recovery email on file. Only the owner of My Crest
                      Club should ever request this.
                    </p>
                    {forgotMasterMsg && (
                      <div
                        data-testid={FORGOT_MASTER_PASSCODE.feedback}
                        className={`mt-4 px-3 py-2 text-sm border ${
                          forgotMasterMsg.type === "success"
                            ? "border-[#B59A5A] bg-[#1A422D]/60 text-[#F9F8F4]"
                            : "border-[#A33] bg-[#5a1a1a]/60 text-[#FFCFCF]"
                        }`}
                      >
                        {forgotMasterMsg.text}
                      </div>
                    )}
                    <div className="mt-4 flex items-center justify-end gap-3">
                      <button
                        type="button"
                        onClick={() => { setForgotMasterOpen(false); setForgotMasterMsg(null); }}
                        data-testid={FORGOT_MASTER_PASSCODE.cancel}
                        className="text-[11px] uppercase tracking-[0.22em] text-[#9aa39a] hover:text-[#D9C68A]"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={requestMasterRecovery}
                        disabled={forgotMasterBusy || (forgotMasterMsg && forgotMasterMsg.type === "success")}
                        data-testid={FORGOT_MASTER_PASSCODE.submit}
                        className="px-4 py-2 text-[11px] uppercase tracking-[0.22em] bg-[#B59A5A] text-[#0f1a14] font-semibold hover:bg-[#A6894B] disabled:opacity-60 transition-colors"
                      >
                        {forgotMasterBusy ? "Sending…" : (forgotMasterMsg && forgotMasterMsg.type === "success" ? "Sent" : "Email Master Passcode")}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </form>
        )}

        <div className="mt-10 flex items-center justify-center gap-2 text-xs text-[#9aa39a]">
          <span>Are you a member?</span>
          <Link to="/login" className="underline underline-offset-4 hover:text-[#D9C68A]">
            Members&rsquo; Portal →
          </Link>
        </div>

        <div className="mt-16 text-center">
          <div className="overline" style={{ color: "#8a8a78" }}>
            My Crest Club · Est. 2026
          </div>
          <div className="mt-3 flex justify-center gap-4 text-[11px] uppercase tracking-[0.22em] text-[#8a8a78]">
            <Link to="/privacy" data-testid="admin-login-footer-privacy" className="hover:text-[#1A422D] transition-colors">Privacy</Link>
            <Link to="/terms" data-testid="admin-login-footer-terms" className="hover:text-[#1A422D] transition-colors">Terms</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
